/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import noop from 'lodash/noop';

import { assertIsServerDeviceId } from '../session/session-attrs';

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 * @typedef {import('../../platform/update-platform').default} UpdatePlatform
 * @typedef {import('../communicator/update/update-result').UpdateResult} UpdateResult
 * @typedef {import('../format-enum').default} FormatEnum
 * @typedef {import('../logger/logger').Logger} Logger
 * @typedef {import('../store/store').default} Store
 */

/**
 * @typedef {Pick<import('../communicator/communicator').default, 'update'>} ICommunicator
 * @typedef {Pick<import('../session/session').Session,
 *   | 'buildId'
 *   | 'deviceId'
 *   | 'id'
 *   | 'serverUrl'
 *   | 'version'
 * >} ISession
 */

/**
 * Aktualizátor zařízení.
 */
class Updater {
  /**
   * Vytvoří aktualizátor.
   * @param {AppManager} appManager
   * @param {string | null} buildId Identifikátor sestavení.
   * @param {ICommunicator} communicator
   * @param {Logger} logger
   * @param {ISession} session Sezení s nastavením zařízení.
   * @param {Store} store Datový sklad s přehrávaným obsahem.
   */
  constructor(appManager, buildId, communicator, logger, session, store) {
    this.appManager = appManager;
    this.buildId = buildId;
    this.communicator = communicator;
    this.logger = logger;
    this.session = session;
    this.store = store;
    this.updatePending = false;
  }

  /**
   * Obnoví nastavení aktualizátoru při startu zařízení.
   */
  async start() {
    if (this.buildId === this.session.buildId) {
      // Pokud je identifikátor sestavení stejný jako při minulém spuštění, tak se nic nedělá.
      return;
    }
    // Pokud je spuštěno jiné sestavení aplikace než minule, aktualizuje se nastavení aplikace.
    await this.store.updateSession(this.session.id, {
      buildId: this.buildId,
      version: null
    });
  }

  // TODO: Refaktorovat na async funkci, protože to řetězené přepisování promis je fakt guláš.
  /**
   * Aktualizuje aplikaci na novou verzi.
   * @param {string | null} version Verze aplikace, na kterou aktualizovat.
   * @returns {Promise<boolean>} True, pokud došlo k aktualizaci verze.
   */
  updateVersion(version) {
    return new Promise((resolve) => {
      const { session } = this;

      if (version === null || !this.buildId || version === session.version) {
        // Pokud není nastavena verze, tak nechceme aktualizovat.
        // Pokud není dostupné buildId (dev verze), tak se aktualizace neprovádí.
        // Pokud se verze aplikace nezměnila, také se aktualizace neprovádí.
        return resolve(false);
      }

      assertIsServerDeviceId(session);
      const { deviceId, serverUrl } = session;

      // Pokud už probíhá aktualizace, vyhodíme výjimku.
      if (this.updatePending) {
        throw new Error('An update is already pending.');
      }
      this.updatePending = true;
      // Provedení aktualizace aplikace.
      return resolve(
        // Detekce platformy na které je aplikace spuštěna.
        this.appManager
          .getUpdatePlatform()
          .then((/** @type {UpdatePlatform} */ updatePlatform) =>
            // Detekce požadovaného formátu aplikace.
            this.appManager
              .getFormat()
              // Vyhledání vhodné aktualizace na serveru.
              .then((/** @type {FormatEnum} */ formatEnum) =>
                this.communicator.update(
                  serverUrl,
                  deviceId,
                  formatEnum,
                  updatePlatform
                )
              )
          )
          .then((/** @type {UpdateResult | null} */ updateResult) => {
            // Pokračujeme v aktualizaci, pouze pokud máme co aktualizovat.
            if (!updateResult) {
              // Tato aktualizace je dokončená.
              return false;
            }
            // Porovná se identifikátor sestavení spuštěné a aktualizované aplikace. Pokud jsou stejné, není třeba aktualizaci provádět.
            const buildIdMatched = this.buildId === updateResult.buildId;
            let updatePromise = Promise.resolve();
            if (!buildIdMatched) {
              // Pokud má aktualizace jiný identifikátor sestavení, tak se aplikace aktualizuje.
              updatePromise = updatePromise.then(() =>
                this.appManager
                  .getUpdateUrlOpaqueInfo()
                  .catch(async (err) => {
                    // Pozor, že nyní když selže getUpdateUrlOpaqueInfo, tak přijde výjimka na Sentry!
                    // A pokud my sami nenastavíme novou URL špatně (to by byl programátorský bug),
                    // tak jediný případ kdy to může nastat je když uživatel poedituje starou URL
                    // ovladačem na zařízení a zmrší ji. V tom případě to máme "robustní" tak,
                    // že k update dojde, ale holt se update informace navždy ztratí.
                    // A právě aby se o této nevratné změně vědělo, tak to explicitně chodí na
                    // Sentry. A na každé takové pojebání záškodníkem následované pokus o update
                    // by měla tato výjimka přijít jenom jednou a nikoliv opakovaně při každém updatu.
                    const errors = [err];
                    let updateUrl;
                    try {
                      updateUrl = await this.appManager.getUpdateUrl();
                    } catch (error) {
                      errors.push(error);
                    }
                    this.logger.error(
                      new AggregateError(
                        errors,
                        `error getting update info from URL: ${updateUrl}`
                      )
                    );
                    return undefined;
                  })
                  .then((info) =>
                    this.appManager.update(updateResult.url, info)
                  )
              );
            }
            updatePromise = updatePromise.then(() =>
              this.store
                .updateSession(session.id, {
                  // Identifikátor sestavení, aby šla detekovat změna verze z jiného zdroje, než je tento aktualizační proces.
                  buildId: updateResult.buildId,
                  // Načtená logická verze, aby se již příště opakovaně nestahovala.
                  version: updateResult.version
                })
                .then(noop)
            );
            if (!buildIdMatched) {
              // Pokud má aktualizace jiný identifikátor sestavení, tak se zařízení po aktualizaci restartuje.
              // Dokončí se aktualizace spuštěním aktualizované aplikace.
              updatePromise = updatePromise.then(() =>
                this.appManager.finishUpdate()
              );
            }
            // Signalizace zda-li došlo k aktualizaci verze.
            return updatePromise.then(() => !buildIdMatched);
          })
          .finally(() => {
            // Odblokují se další aktualizace.
            this.updatePending = false;
          })
      );
    });
  }
}

export default Updater;
